import { StepType } from '@/types/automationFields';
import type {
  AutomationStep,
  Condition,
  ConditionalStep,
  FailureHandlerStep,
} from '@/types/automationSteps';
import { UniqueIdentifier } from '@dnd-kit/core';

type ChildSlot =
  | { kind: 'direct' } // Iterate/Repeat/Retry children
  | { kind: 'condition'; condition_id: string } // Conditional branch children
  | { kind: 'failure_handler'; group: 'protected' | 'handler' };

function isConditionalStep(step: AutomationStep): step is ConditionalStep {
  return step.step_type === StepType.Conditional;
}

function isFailureHandlerStep(step: AutomationStep): step is FailureHandlerStep {
  return step.step_type === StepType.FailureHandler;
}

function hasDirectChildren(
  step: AutomationStep,
): step is AutomationStep & { children: AutomationStep[] } {
  return (
    (step.step_type === StepType.Iterate ||
      step.step_type === StepType.Repeat ||
      step.step_type === StepType.Retry) &&
    Array.isArray((step as any).children)
  );
}

/**
 * Returns all child "slots" a step exposes.
 * This is the only place you encode step-shape differences.
 */
function getChildSlots(step: AutomationStep): ChildSlot[] {
  if (hasDirectChildren(step)) return [{ kind: 'direct' }];
  if (isFailureHandlerStep(step)) {
    return [
      { kind: 'failure_handler', group: 'protected' },
      { kind: 'failure_handler', group: 'handler' },
    ];
  }
  if (isConditionalStep(step))
    return step.conditions.map((c) => ({ kind: 'condition', condition_id: c.condition_id }));
  return [];
}

function readChildren(step: AutomationStep, slot: ChildSlot): AutomationStep[] {
  if (slot.kind === 'direct' && hasDirectChildren(step)) return step.children ?? [];
  if (slot.kind === 'condition' && isConditionalStep(step)) {
    return step.conditions.find((c) => c.condition_id === slot.condition_id)?.children ?? [];
  }
  if (slot.kind === 'failure_handler' && isFailureHandlerStep(step)) {
    return slot.group === 'handler' ? step.handler_children : step.children;
  }
  return [];
}

function writeChildren(
  step: AutomationStep,
  slot: ChildSlot,
  nextChildren: AutomationStep[],
): AutomationStep {
  if (slot.kind === 'direct' && hasDirectChildren(step)) {
    return { ...step, children: nextChildren } as AutomationStep;
  }

  if (slot.kind === 'condition' && isConditionalStep(step)) {
    const nextConds: Condition[] = step.conditions.map((c) =>
      c.condition_id === slot.condition_id ? { ...c, children: nextChildren } : c,
    );
    return { ...step, conditions: nextConds };
  }

  if (slot.kind === 'failure_handler' && isFailureHandlerStep(step)) {
    return slot.group === 'handler'
      ? { ...step, handler_children: nextChildren }
      : { ...step, children: nextChildren };
  }

  return step; // shouldn't happen; defensive
}

/** Remove a node from a list (recursing into any child slots) */
function removeStepImmutable(
  list: AutomationStep[],
  targetId: UniqueIdentifier,
): { removed: AutomationStep | null; tree: AutomationStep[] } {
  let removed: AutomationStep | null = null;

  const tree = list
    .map((node) => {
      if (String(node.step_id) === String(targetId)) {
        removed = node;
        return null;
      }

      for (const slot of getChildSlots(node)) {
        const kids = readChildren(node, slot);
        if (!kids.length) continue;

        const res = removeStepImmutable(kids, targetId);
        if (res.removed) {
          removed = res.removed;
          return writeChildren(node, slot, res.tree);
        }
      }

      return node;
    })
    .filter(Boolean) as AutomationStep[];

  return { removed, tree };
}

/**
 * Insert a node under:
 * - root if parentId === null
 * - direct children of parent (iterate/repeat/retry) when conditionId is null/undefined
 * - conditional branch children when conditionId is provided
 *
 * Returns inserted flag so we never "drop" the moved node on failure.
 */
function insertStepImmutable(
  list: AutomationStep[],
  parentId: UniqueIdentifier | null,
  index: number,
  nodeToInsert: AutomationStep,
  conditionId?: string | null,
): { tree: AutomationStep[]; inserted: boolean } {
  if (parentId == null) {
    const next = [...list];
    next.splice(index, 0, nodeToInsert);
    return { tree: next, inserted: true };
  }

  let inserted = false;

  const tree = list.map((node) => {
    if (inserted) return node;

    if (String(node.step_id) === String(parentId)) {
      // Choose which slot we insert into:
      const desiredSlot: ChildSlot | null = isFailureHandlerStep(node)
        ? { kind: 'failure_handler', group: conditionId === 'handler' ? 'handler' : 'protected' }
        : conditionId != null
          ? { kind: 'condition', condition_id: conditionId }
          : { kind: 'direct' };

      const kids = readChildren(node, desiredSlot);
      const nextKids = [...kids];
      nextKids.splice(index, 0, nodeToInsert);

      const updated = writeChildren(node, desiredSlot, nextKids);
      // Only count as inserted if the write actually took effect (slot existed)
      inserted = updated !== node;
      return updated;
    }

    // recurse into all child slots
    for (const slot of getChildSlots(node)) {
      const kids = readChildren(node, slot);
      if (!kids.length) continue;

      const res = insertStepImmutable(kids, parentId, index, nodeToInsert, conditionId);
      if (res.inserted) {
        inserted = true;
        return writeChildren(node, slot, res.tree);
      }
    }

    return node;
  });

  return { tree, inserted };
}

export function moveStepImmutable(
  tree: AutomationStep[],
  movedId: UniqueIdentifier,
  newParentId: UniqueIdentifier | null,
  newIndex: number,
  conditionId?: string | null,
): AutomationStep[] {
  const { removed, tree: without } = removeStepImmutable(tree, movedId);
  if (!removed) return tree;

  const res = insertStepImmutable(without, newParentId, newIndex, removed, conditionId);

  // Safety: don't lose the node if destination is invalid
  if (!res.inserted) return tree;

  return res.tree;
}
